import { useMemo } from "react";
import { useIntl } from "react-intl";
import { z } from "zod";

import { useField } from "@rvf/react-router";
import { withZod } from "@rvf/zod";

import { FieldError, Form } from "~/components/forms/Form";
import { Button } from "~/components/ui/button";
import { RadioGroup, RadioGroupItem } from "~/hec/components/ui/radio-group";
import type { TransformedModule } from "~/hooks/learning-center/transformers";
import { cn } from "~/lib/utils";

import messages from "./messages";

/**
 * SFCC-11505 — port of composable `quizForm.jsx`. Radio-only (single-choice)
 * quiz; each question is required. Answers submit as stringified indices
 * (`{ "question-0": "1", … }`) so the state machine can score them against
 * `correctAnswerIndex` (all-or-nothing).
 *
 * Composable used react-hook-form; this storefront standardizes on RVF + Zod
 * (FORMS_GUIDE.md). Because the quiz is scored CLIENT-SIDE (no route action),
 * the form runs in `submitSource="state"` mode with a `handleSubmit` callback —
 * RVF validates, then hands the parsed answers to `onSubmit` with no navigation.
 */
type QuizQuestionData = NonNullable<
  TransformedModule["quiz"]
>["questions"][number];

interface QuizFormProps {
  module: TransformedModule;
  requestInProgress: boolean;
  onSubmit: (data: Record<string, string>) => void;
}

/** One radio question, wired to RVF via a controlled field (Radix RadioGroup). */
const QuizQuestion = ({
  index,
  question,
}: {
  index: number;
  question: QuizQuestionData;
}) => {
  const field = useField(`question-${index}`);
  const control = field.getControlProps();
  const error = field.error();

  return (
    <fieldset
      className={cn("space-y-5", index > 0 && "mt-[2.6875rem]")}
      data-testid="quizForm-FormItem"
    >
      <legend className="font-serif text-sm font-medium leading-normal text-black lg:text-base">
        {question.question}
      </legend>
      <RadioGroup
        value={(control.value as string | undefined) ?? ""}
        onValueChange={control.onChange}
        onBlur={control.onBlur}
        className="flex flex-col space-y-[1.1875rem]"
        data-testid="quizForm-RadioGroup"
      >
        {question.answers.map((answer, answerIndex) => {
          const id = `question-${index}-answer-${answerIndex}`;
          return (
            <div key={id} className="flex items-center space-x-4">
              <RadioGroupItem
                value={answerIndex.toString()}
                id={id}
                data-testid="quizForm-RadioGroupItem"
              />
              <label
                htmlFor={id}
                className="text-sm font-light leading-normal text-emphasis lg:text-base"
              >
                {answer}
              </label>
            </div>
          );
        })}
      </RadioGroup>
      {error && (
        <FieldError data-testid="quizForm-FormMessage">{error}</FieldError>
      )}
    </fieldset>
  );
};

const QuizForm = ({ module, requestInProgress, onSubmit }: QuizFormProps) => {
  const intl = useIntl();
  const questions = module.quiz?.questions ?? [];
  const requiredMsg = intl.formatMessage(messages.quizRequiredAnswer);

  const validator = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {};
    questions.forEach((_, i) => {
      shape[`question-${i}`] = z
        .string({ required_error: requiredMsg })
        .min(1, requiredMsg);
    });
    return withZod(z.object(shape));
    // Rebuild when the question count or the localized message changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length, requiredMsg]);

  const defaultValues = useMemo(() => {
    const values: Record<string, string> = {};
    questions.forEach((_, i) => {
      values[`question-${i}`] = "";
    });
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  return (
    <Form
      validator={validator}
      defaultValues={defaultValues}
      submitSource="state"
      handleSubmit={(data: Record<string, string>) => onSubmit(data)}
      data-testid="quizForm"
    >
      {questions.map((question, index) => (
        <QuizQuestion
          key={`question-${index}`}
          index={index}
          question={question}
        />
      ))}
      <div className="mt-[3.25rem] flex">
        <Button
          type="submit"
          variant="cta"
          disabled={requestInProgress}
          className="w-full lg:ml-auto lg:w-[14.1875rem]"
          data-testid="quizForm-Button"
        >
          {intl.formatMessage(messages.quizSubmit)}
        </Button>
      </div>
    </Form>
  );
};

export default QuizForm;
